import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SendRfqDialog = ({ target, vendors, onClose, onSent }) => {
  const { getAuthHeader } = useAuth();
  const [vendorIds, setVendorIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const match = vendors.find((v) => v.name.toLowerCase() === (target.vendor_name || '').toLowerCase());
    setVendorIds(match ? [match.id] : []);
  }, [target, vendors]);

  const toggleVendor = (vendorId) => {
    setVendorIds((prev) =>
      prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId]
    );
  };

  const handleSend = async () => {
    if (vendorIds.length === 0) { toast.error('Select at least one vendor'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/requisitions/${target.id}/send-rfq`,
        { vendor_ids: vendorIds }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send RFQ');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Quote — {target.reagent_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-surface rounded text-sm">
            <p><span className="text-text-tertiary">Catalogue #:</span> <span className="font-mono">{target.catalogue_number}</span></p>
            <p className="mt-1"><span className="text-text-tertiary">Requested vendor:</span> {target.vendor_name}</p>
          </div>
          <p className="text-sm text-text-secondary">
            Select <strong>one or more vendors</strong> to request quotes from. Each vendor receives its own separate email (to all their contacts) — vendors never see each other. Replies come directly to your email.
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {vendors.length === 0 && (
              <p className="text-xs text-amber-700">No vendors in the directory yet — add one on the Vendors page first.</p>
            )}
            {vendors.map((v) => (
              <label
                key={v.id}
                className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
                  vendorIds.includes(v.id) ? 'border-accent-brand bg-accent-brand-subtle' : 'border-border-default hover:bg-surface'
                }`}
                data-testid={`rfq-vendor-option-${v.id}`}
              >
                <Checkbox
                  checked={vendorIds.includes(v.id)}
                  onCheckedChange={() => toggleVendor(v.id)}
                  data-testid={`rfq-vendor-checkbox-${v.id}`}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-text-primary">{v.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {v.emails.map((em) => (
                      <span key={em} className="px-1.5 py-0.5 rounded-full text-xs bg-white border border-border-default text-text-secondary font-mono">{em}</span>
                    ))}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <Button
            className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white"
            disabled={sending || vendorIds.length === 0}
            onClick={handleSend}
            data-testid="confirm-send-rfq-button"
          >
            {sending ? 'Sending…' : `Send Quote Request${vendorIds.length > 1 ? `s (${vendorIds.length} vendors)` : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendRfqDialog;
